import { AddCircle, Delete, Edit } from "@mui/icons-material";
import {
    Box,
    Button,
    FormControlLabel,
    FormGroup,
    FormLabel,
    IconButton,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import {
    MRT_ColumnFiltersState as MRTColumnFiltersState,
    MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
    MRT_PaginationState as MRTPaginationState,
    MRT_SortingState as MRTSortingState,
    MRT_ToggleDensePaddingButton as MRTToggleDensePaddingButton,
    MRT_ToggleFiltersButton as MRTToggleFiltersButton,
    MRT_ToggleGlobalFilterButton as MRTToggleGlobalFilterButton,
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Row,
} from "material-react-table";
import { useConfirm } from "material-ui-confirm";
import { compose } from "ramda";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { GlobalFilter } from "../../../classes/filter-state.class";
import { Pagination } from "../../../classes/pagination.class";
import { ResponsePagination } from "../../../classes/response-pagination.class";
import TextField from "../../../components/Common/Input/TextField/TextField";
import { ManageModal } from "../../../components/Common/ManageModal/ManageModal";
import withAppRouter from "../../../components/HoC/withAppRouter";
import Status from "../../../components/Status/Status";
import { useAxios } from "../../../contexts/axios.context";
import { Shape } from "../../../utils/ValidateUtils";
import { UnitDto } from "./dto/unit.dto";

const validationSchema = yup.object<Shape<UnitDto>>({
    name: yup.string().trim().required("Enter Unit Name").max(100, "Unit Name cannot be longer than 100 characters."),
});
const PREFIX_API = `/ghg/master-unit`;
const Category = () => {
    const { post } = useAxios();
    const confirm = useConfirm();
    const [datasource, setDatasource] = useState<Array<UnitDto>>([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [columnFilters, setColumnFilters] = useState<MRTColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState<GlobalFilter<UnitDto>>({ filters: [], search: "" });
    const [sorting, setSorting] = useState<MRTSortingState>([]);
    const [editData, setEditData] = useState<UnitDto>();
    const [pagination, setPagination] = useState<MRTPaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await post<ResponsePagination<UnitDto>>(`${PREFIX_API}/findByCriteria`, {
                criteria: { columnFilters, globalFilter, sorting },
                pagination: { currentPage: pagination.pageIndex + 1, limit: pagination.pageSize } as Pagination,
            });
            setDatasource(data.datasource);
            setRowCount(data.totalRecord);
        } catch (error) {
            setIsError(true);
            console.error(error);
            return;
        }
        setIsError(false);
        setIsLoading(false);
    }, [post, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeleteRow = useCallback(
        (row: MRT_Row<UnitDto>) => {
            const id: number = row.original.id!;
            const name: string = row.getValue("name") as string;
            confirm({
                description: (
                    <Typography component="span">
                        Are you sure you want to delete{" "}
                        <Typography component="span" color="lightsalmon" fontWeight="600">
                            {name}
                        </Typography>
                    </Typography>
                ),
            })
                .then(async () => {
                    await post(`${PREFIX_API}/delete/${id}`);
                    fetchData();
                })
                .catch(() => undefined);
        },
        [confirm, post, fetchData]
    );

    const columns = useMemo<MRT_ColumnDef<UnitDto>[]>(
        () => [
            {
                accessorKey: "name",
                header: "Unit Name",
                size: 140,
            },
            {
                accessorKey: "isActive",
                header: "Usage Status",
                enableColumnFilter: false,
                Cell: ({ cell }) => (
                    <Status
                        size="medium"
                        status={cell.getValue() ? "success" : "error"}
                        label={cell.getValue() ? "Active" : "Inactive"}
                    />
                ),
            },
            {
                accessorKey: "createdAt",
                header: "Created By",
                enableEditing: false,
                enableColumnFilter: false,
                size: 80,
                Cell: ({ cell }) => (
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Typography variant="caption">{cell.row.original.createdBy}</Typography>
                        <Typography variant="caption">
                            {dayjs(cell.row.original.createdAt).format("DD/MM/YYYY")}
                        </Typography>
                    </Stack>
                ),
                muiTableHeadCellProps: {
                    align: "center",
                },
            },
            {
                accessorKey: "updatedAt",
                header: "Updated By",
                enableEditing: false,
                enableColumnFilter: false,
                size: 80,
                Cell: ({ cell }) => (
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Typography variant="caption">{cell.row.original.updatedBy}</Typography>
                        <Typography variant="caption">
                            {dayjs(cell.row.original.updatedAt).format("DD/MM/YYYY")}
                        </Typography>
                    </Stack>
                ),
                muiTableHeadCellProps: {
                    align: "center",
                },
            },
        ],
        []
    );
    const [manageModalOpen, setManageModalOpen] = useState(false);
    const handleOnEdit = useCallback(
        (row: MRT_Row<UnitDto>) => {
            setManageModalOpen(true);
            setEditData({ ...row.original });
        },
        [setManageModalOpen]
    );
    const onFormSubmit = async (values: UnitDto) => {
        if (editData?.id) {
            await post(`${PREFIX_API}/update/${editData?.id}`, { ...values });
        } else {
            await post(`${PREFIX_API}`, { ...values });
        }
        setManageModalOpen(false);
        formik.setSubmitting(false);
        formik.resetForm();
        fetchData();
    };

    const formik = useFormik<UnitDto>({
        initialValues: {
            name: "",
            isActive: true,
        },
        onSubmit: onFormSubmit,
        validationSchema,
    });
    const { setValues } = formik;
    useEffect(() => {
        if (editData)
            setValues({
                name: editData?.name || "",
                isActive: editData?.isActive ?? true,
            });
    }, [setValues, editData]);

    return (
        <Fragment>
            <MaterialReactTable
                muiTableContainerProps={{ sx: { maxHeight: `calc(100vh - 120px - 96px)` } }}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        muiTableHeadCellProps: {
                            align: "center",
                        },
                        size: 120,
                    },
                }}
                getRowId={(row) => row.id?.toString()!}
                rowCount={rowCount}
                initialState={{
                    showColumnFilters: true,
                }}
                columns={columns}
                data={datasource}
                editingMode="modal" //default
                enableRowNumbers
                rowNumberMode="static"
                enableEditing
                enableHiding={false}
                muiToolbarAlertBannerProps={isError ? { color: "error", children: "Error loading data" } : undefined}
                state={{
                    columnFilters,
                    globalFilter: globalFilter.search,
                    isLoading,
                    pagination,
                    showAlertBanner: isError,
                    sorting,
                }}
                manualPagination
                onColumnFiltersChange={setColumnFilters}
                onGlobalFilterChange={(search) => setGlobalFilter((prev) => ({ ...prev, search }))}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                renderRowActions={({ row }) => (
                    <Box sx={{ display: "flex", gap: "1rem" }}>
                        <Tooltip arrow placement="left" title="Edit">
                            <IconButton onClick={() => handleOnEdit(row)}>
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        <Tooltip arrow placement="right" title="Delete">
                            <IconButton color="error" onClick={() => handleDeleteRow(row)}>
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
                renderTopToolbarCustomActions={() => (
                    <Box sx={{ display: "flex", gap: "1rem", p: "4px" }}>
                        <Button
                            color="secondary"
                            onClick={() => {
                                setManageModalOpen(true);
                                setEditData(undefined);
                            }}
                            variant="contained"
                            startIcon={<AddCircle />}
                            size="small"
                        >
                            Create New
                        </Button>
                    </Box>
                )}
                renderToolbarInternalActions={({ table }) => (
                    <Box>
                        <MRTToggleGlobalFilterButton table={table} />
                        <MRTToggleFiltersButton table={table} />
                        <MRTToggleDensePaddingButton table={table} />
                        <MRTFullScreenToggleButton table={table} />
                    </Box>
                )}
            />
            <ManageModal<UnitDto>
                open={manageModalOpen}
                onClose={() => setManageModalOpen(false)}
                title={!editData ? "Create New Unit" : "Edit Unit"}
                formik={formik}
                formComponent={
                    <Fragment>
                        <TextField
                            variant="standard"
                            value={formik.values.name}
                            key="name"
                            name="name"
                            label="Name"
                            onChange={formik.handleChange}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                        />
                        <FormGroup>
                            <FormLabel>Settings</FormLabel>
                            <FormControlLabel
                                control={
                                    <Switch
                                        id="isActive"
                                        checked={formik.values.isActive}
                                        value={formik.values.isActive}
                                        onChange={() => {
                                            const previous = formik.values.isActive;
                                            formik.setFieldValue("isActive", !previous);
                                        }}
                                    />
                                }
                                label="Active"
                            />
                        </FormGroup>
                    </Fragment>
                }
            />
            <Box component="div"></Box>
        </Fragment>
    );
};

const CategoryWrapped = compose(withAppRouter);
export default CategoryWrapped(Category);
